// Side-by-side overlay viewer for comparing two issuances of the same
// drawing. Renders both PDFs to canvases, recolors each side's linework
// to its chosen swatch, then layers them with `mix-blend-mode: multiply`
// so the white backgrounds preserve and the linework intersections show
// as a darker mix (signaling "both versions have this here").
//
// State plumbing:
//   - compareConfig from useStore drives what gets rendered.
//   - Local state owns the rendered ImageData snapshots so color changes
//     are instant (just re-tint the existing pixels) without re-running
//     pdf.js.
//
// Out-of-scope for this commit (follow-up): manual drag alignment,
// measure tool integration. Auto alignment is identity transform — both
// canvases share the same origin and scale.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useStore } from '../store/useStore';
import {
  COMPARE_SWATCHES,
  rgbForSwatch,
  renderSheetForCompare,
  recolorCanvasInPlace,
} from '../utils/compareRender';
import styles from './CompareViewer.module.css';

// Render scale: high enough for crisp linework on big monitors, low enough
// that two E-size canvases at this scale don't blow past tab memory.
// Ballpark: E-size at scale 1.5 ≈ 3700x4750 px ≈ 70 MB raw × 2 = 140 MB.
const RENDER_SCALE = 1.5;

export default function CompareViewer() {
  const { compareConfig, exitCompare } = useStore();

  const [colorA, setColorA] = useState(compareConfig?.colorA_id || 'red');
  const [colorB, setColorB] = useState(compareConfig?.colorB_id || 'blue');
  const [showA, setShowA]   = useState(true);
  const [showB, setShowB]   = useState(true);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [size,    setSize]    = useState({ w: 0, h: 0 });

  // Cached raw pixel data per side — keeps color changes O(pixels) instead
  // of O(pdf.js render), so swapping colors feels instant.
  const aDataRef = useRef(null); // { canvas, original ImageData }
  const bDataRef = useRef(null);

  const canvasARef = useRef(null);
  const canvasBRef = useRef(null);

  // Pan + zoom state on the overlay group.
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const panActiveRef = useRef(false);
  const panStartRef  = useRef({ x: 0, y: 0 });
  const panAtStartRef = useRef({ x: 0, y: 0 });

  // ── Render both sides ────────────────────────────────────────────────
  useEffect(() => {
    if (!compareConfig) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    aDataRef.current = null;
    bDataRef.current = null;

    (async () => {
      try {
        const [aRes, bRes] = await Promise.all([
          renderSheetForCompare(compareConfig.sheetA_id, RENDER_SCALE),
          renderSheetForCompare(compareConfig.sheetB_id, RENDER_SCALE),
        ]);
        if (cancelled) return;

        // Use the larger of the two as the canvas size — rare for the same
        // sheet number to render at meaningfully different point sizes
        // across versions, but not impossible if a designer changes the
        // sheet rotation between issuances.
        const w = Math.max(aRes.width, bRes.width);
        const h = Math.max(aRes.height, bRes.height);
        setSize({ w, h });

        aDataRef.current = aRes;
        bDataRef.current = bRes;

        applyColor('A', colorA);
        applyColor('B', colorB);
        setLoading(false);

        // Center initial pan around the shared canvas.
        if (containerRef.current) {
          const c = containerRef.current.getBoundingClientRect();
          setPan({
            x: (c.width  - w) / 2,
            y: (c.height - h) / 2,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to render');
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareConfig?.sheetA_id, compareConfig?.sheetB_id]);

  // ── Recolor a side without re-rendering the PDF ──────────────────────
  const applyColor = useCallback((side, swatchId) => {
    const data = side === 'A' ? aDataRef.current : bDataRef.current;
    const canvasRef = side === 'A' ? canvasARef : canvasBRef;
    if (!data || !canvasRef.current) return;
    const targetCanvas = canvasRef.current;
    if (targetCanvas.width !== data.width || targetCanvas.height !== data.height) {
      targetCanvas.width  = data.width;
      targetCanvas.height = data.height;
    }
    // Make the visible canvas mirror the offscreen render data so we have
    // pixels to recolor against.
    const ctx = targetCanvas.getContext('2d');
    ctx.putImageData(data.originalImageData, 0, 0);
    recolorCanvasInPlace(targetCanvas, data.originalImageData, rgbForSwatch(swatchId));
  }, []);

  // Re-tint when the user changes a color swatch.
  useEffect(() => { if (!loading) applyColor('A', colorA); }, [colorA, loading, applyColor]);
  useEffect(() => { if (!loading) applyColor('B', colorB); }, [colorB, loading, applyColor]);

  // ── Pan / zoom interaction ───────────────────────────────────────────
  function onWheel(e) {
    if (!containerRef.current) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    const next = Math.min(8, Math.max(0.1, zoom * (1 + delta)));
    // Zoom toward the cursor position so things stay where the user expects.
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    setPan((prev) => ({
      x: cx - ((cx - prev.x) * (next / zoom)),
      y: cy - ((cy - prev.y) * (next / zoom)),
    }));
    setZoom(next);
  }

  function onPointerDown(e) {
    if (e.button !== 0) return;
    panActiveRef.current = true;
    panStartRef.current  = { x: e.clientX, y: e.clientY };
    panAtStartRef.current = { ...pan };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e) {
    if (!panActiveRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setPan({
      x: panAtStartRef.current.x + dx,
      y: panAtStartRef.current.y + dy,
    });
  }
  function onPointerUp(e) {
    panActiveRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  }

  if (!compareConfig) return null;

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <div
          ref={containerRef}
          className={styles.canvasArea}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: 'none', cursor: panActiveRef.current ? 'grabbing' : 'grab' }}
        >
          {loading && (
            <div className={styles.overlay}>
              <span>Rendering both versions…</span>
            </div>
          )}
          {error && (
            <div className={styles.overlay}>
              <span className={styles.errorText}>Failed to compare: {error}</span>
            </div>
          )}

          <div
            className={styles.stack}
            style={{
              width:  size.w,
              height: size.h,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              visibility: loading ? 'hidden' : 'visible',
            }}
          >
            {/* Side A goes underneath; B sits on top with multiply blend so
                white BG of B doesn't wipe out A's content. */}
            <canvas
              ref={canvasARef}
              className={styles.layer}
              style={{ visibility: showA ? 'visible' : 'hidden' }}
            />
            <canvas
              ref={canvasBRef}
              className={`${styles.layer} ${styles.layerTop}`}
              style={{
                visibility: showB ? 'visible' : 'hidden',
                mixBlendMode: 'multiply',
              }}
            />
          </div>
        </div>

        {/* Right-rail tools panel ─────────────────────────────────────── */}
        <aside className={styles.tools}>
          <div className={styles.toolsHeader}>
            <span>COMPARE — {compareConfig.sheet_number || ''}</span>
            <button
              className={styles.exitBtn}
              onClick={exitCompare}
              title="Back to the regular sheet viewer"
            >
              ✕ EXIT
            </button>
          </div>

          <ToolsSide
            label="A"
            color={colorA}
            otherColor={colorB}
            onColorChange={setColorA}
            shown={showA}
            onShownChange={setShowA}
          />
          <ToolsSide
            label="B"
            color={colorB}
            otherColor={colorA}
            onColorChange={setColorB}
            shown={showB}
            onShownChange={setShowB}
          />

          <div className={styles.toolsFootHint}>
            Manual alignment + measurements coming next. For now both sides
            overlay at their native origin.
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── Right-panel side controls ──────────────────────────────────────────────

function ToolsSide({ label, color, otherColor, onColorChange, shown, onShownChange }) {
  return (
    <div className={styles.toolsSection}>
      <div className={styles.toolsRowHead}>
        <span className={styles.toolsSideLabel}>SIDE {label}</span>
        <label className={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={shown}
            onChange={(e) => onShownChange(e.target.checked)}
          />
          <span>show</span>
        </label>
      </div>
      <div className={styles.swatchRow}>
        {COMPARE_SWATCHES.map((s) => {
          const isOther = s.id === otherColor;
          const isSelected = s.id === color;
          return (
            <button
              key={s.id}
              type="button"
              className={`${styles.swatch} ${isSelected ? styles.swatchSelected : ''}`}
              style={{ background: `rgb(${s.rgb.join(',')})` }}
              onClick={() => onColorChange(s.id)}
              disabled={isOther}
              title={isOther ? `${s.label} is in use on the other side` : s.label}
            />
          );
        })}
      </div>
    </div>
  );
}
